import { CREDIT_MICROS } from '@/billing/api';
import { percentText } from '@/billing/plan-math';
import { formatNumber } from '@/workspace/artifacts/money';

const DAY_MS = 86_400_000;

/** UTC calendar days as `YYYY-MM-DD`, the overview's day keys. */
export const isoDay = (date: Date) => date.toISOString().slice(0, 10);
export const dayTime = (day: string) => Date.parse(`${day}T00:00:00Z`);
export const addDays = (day: string, count: number) =>
  isoDay(new Date(dayTime(day) + count * DAY_MS));
export const daysBetween = (from: string, to: string) =>
  Math.round((dayTime(to) - dayTime(from)) / DAY_MS);
export const todayIso = (now = new Date()) => isoDay(now);

export const credits = (micros: number | null | undefined) => (micros ?? 0) / CREDIT_MICROS;

export interface Formatters {
  locale: string;
  /** Whole credits with grouping: 1 284. */
  int(value: number): string;
  /** Whole credits, or one decimal for small fractional amounts. */
  amount(value: number): string;
  /** Always one decimal: averages. */
  one(value: number): string;
  percent(value: number): string;
  day(day: string): string;
  longDay(day: string): string;
  date(iso: string): string;
  time(iso: string): string;
  monthTitle(year: number, month: number): string;
  weekdays: string[];
}

// Numbers follow the app's one rule: space thousand groups in every language
// (workspace/artifacts/money.ts), the language's own decimal separator.
const numberFormat = (locale: string, options: Intl.NumberFormatOptions) => ({
  format: (value: number) => formatNumber(value, locale, options),
});

export function formatters(locale: string): Formatters {
  const whole = numberFormat(locale, { maximumFractionDigits: 0 });
  const tenth = numberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const small = numberFormat(locale, { maximumFractionDigits: 1 });
  const short = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
  const long = new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
  const localDate = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });
  const localTime = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' });
  const month = new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  const weekday = new Intl.DateTimeFormat(locale, { weekday: 'narrow', timeZone: 'UTC' });
  // 2024-01-01 is a Monday.
  const weekdays = Array.from({ length: 7 }, (_, i) =>
    weekday.format(new Date(Date.UTC(2024, 0, 1 + i))),
  );
  return {
    locale,
    int: (value) => whole.format(Math.round(value)),
    amount: (value) =>
      Math.abs(value) < 10 && Math.round(value) !== value
        ? small.format(value)
        : whole.format(Math.round(value)),
    one: (value) => tenth.format(value),
    percent: (value) => percentText(value, locale),
    day: (day) => short.format(new Date(dayTime(day))),
    longDay: (day) => long.format(new Date(dayTime(day))),
    date: (iso) => localDate.format(new Date(iso)),
    time: (iso) => localTime.format(new Date(iso)),
    monthTitle: (year, m) => month.format(new Date(Date.UTC(year, m, 1))),
    weekdays,
  };
}

/** Whole percentages that always add up to 100 (largest remainder). */
export function shares(values: number[]): number[] {
  const total = values.reduce((sum, value) => sum + value, 0);
  if (!total) return values.map(() => 0);
  const raw = values.map((value) => (value / total) * 100);
  const floor = raw.map((value) => Math.floor(value));
  const remainder = 100 - floor.reduce((sum, value) => sum + value, 0);
  const order = raw
    .map((value, index) => [value - floor[index], index] as const)
    .sort((a, b) => b[0] - a[0]);
  for (let j = 0; j < remainder && j < order.length; j++) floor[order[j][1]] += 1;
  return floor;
}

/** A readable axis of whole credits: a rounded top and at most `maxTicks` whole steps. */
export function niceScale(max: number, maxTicks: number): { top: number; ticks: number[] } {
  if (!(max > 0)) return { top: 10, ticks: [0, 5, 10] };
  // Labels are whole credits, so a step below 1 would print the same label twice (0, 1, 1).
  const magnitude = Math.max(1, Math.pow(10, Math.floor(Math.log10(max / maxTicks))));
  const multiples = magnitude >= 10 ? [1, 2, 2.5, 5, 10] : [1, 2, 5, 10];
  let step = 10 * magnitude;
  for (const multiple of multiples) {
    if (Math.ceil(max / (multiple * magnitude)) <= maxTicks) {
      step = multiple * magnitude;
      break;
    }
  }
  const top = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let value = 0; value <= top + 1e-9; value += step) ticks.push(Number(value.toFixed(6)));
  return { top, ticks };
}

export const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');

export interface AxisTick {
  label: string;
  pos: number;
  edge: 'start' | 'end' | null;
  minor: boolean;
}

/** Day labels under a daily chart: every day for short ranges, Mondays otherwise. */
export function dayTicks(
  days: string[],
  posOf: (index: number) => number,
  f: Formatters,
): AxisTick[] {
  const ticks: AxisTick[] = [];
  let count = 0;
  const step = Math.max(1, Math.ceil(days.length / 60)) * 7;
  days.forEach((day, index) => {
    const weekday = new Date(dayTime(day)).getUTCDay();
    const show = days.length <= 10 || (weekday === 1 && (days.length <= 120 || index % step < 7));
    if (!show) return;
    const pos = posOf(index);
    ticks.push({
      label: f.day(day),
      pos,
      edge: pos < 4 ? 'start' : pos > 96 ? 'end' : null,
      minor: days.length > 21 && count % 2 === 1,
    });
    count++;
  });
  return ticks;
}

/** The plan's monthly credits from its id (`credits-4600`). */
export function planCredits(
  planId: string | null | undefined,
  plans: { id: string; credits: number }[] = [],
) {
  if (!planId) return null;
  const listed = plans.find((plan) => plan.id === planId);
  if (listed) return listed.credits;
  const value = Number(planId.slice(planId.lastIndexOf('-') + 1));
  return Number.isSafeInteger(value) ? value : null;
}
